import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Anatomy from './components/Anatomy';
import Manual from './components/Manual';
import Specs from './components/Specs';
import LabProof from './components/LabProof';
import Footer from './components/Footer';
import Legacy from './components/Legacy';
import Comparison from './components/Comparison';
import Blog from './components/Blog';
import LogTeaser from './components/LogTeaser';
import SyndicateMembership from './components/SyndicateMembership';
import FAQ from './components/FAQ';
import VesselConfigurator from './components/VesselConfigurator';
import PressMarquee from './components/PressMarquee';
import { BrandProvider } from './context/BrandContext';
import { UserProvider } from './context/UserContext';
import { CartProvider } from './context/CartContext';
import { FirebaseProvider } from './context/FirebaseContext';
import { motion, AnimatePresence } from 'motion/react';

import ExplorerTV from './components/ExplorerTV';
import NightExpeditions from './components/NightExpeditions';
import TechnicalDifferentiators from './components/TechnicalDifferentiators';
import LoginPage from './components/LoginPage';
import AdminLoginPage from './components/AdminLoginPage';
import Membership from './components/Membership';
import TheLog from './components/TheLog';
import { MemberPortal } from './components/MemberPortal';
import { MultiStepCheckout } from './components/MultiStepCheckout';
import { MembershipCheckout } from './components/MembershipCheckout';
import { CartDrawer } from './components/CartDrawer';
import Equipment from './components/Equipment';
import EquipmentDetail from './components/EquipmentDetail';
import EndurancePage from './components/EndurancePage';

// Lazy loaded large components
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const Mission3DMap = lazy(() => import('./components/Mission3DMap'));

import Coaching from './components/Coaching';
import TeamBuilding from './components/TeamBuilding';
import GiftCards from './components/GiftCards';
import SyndicatePage from './components/SyndicatePage';
import DeploymentClearance from './components/DeploymentClearance';
import Waiver from './components/Waiver';
import GalleryView from './components/GalleryView';
import ServicesOverview from './components/ServicesOverview';
import SuccessPage from './components/SuccessPage';
import PageTransition from './components/PageTransition';
import GeneralExpeditions from './components/GeneralExpeditions';
import HowItWorks from './components/HowItWorks';
import ProgressionPathway from './components/ProgressionPathway';
import SafetyFirst from './components/SafetyFirst';
import SocialProof from './components/SocialProof';
import Contact from './components/Contact';
import VerifyProspectPage from './components/VerifyProspectPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import FullWaiver from './components/FullWaiver';

function ScrollToAnchor() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 550); // Wait for the AnimatePresence mode="wait" transition to complete

    return () => clearTimeout(timeout);
  }, [hash, pathname]);

  return null;
}

function LandingPage() {
  return (
    <>
      <Hero />
      <Legacy />
      <HowItWorks />
      <SocialProof />
      <ProgressionPathway />
      <LogTeaser />
      <Anatomy />
      <VesselConfigurator />
      <SafetyFirst />
      <NightExpeditions />
      <SyndicateMembership />
      <FAQ />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
        <Route path="/coaching" element={<PageTransition><Coaching /></PageTransition>} />
        <Route path="/team-building" element={<PageTransition><TeamBuilding /></PageTransition>} />
        <Route path="/gift" element={<PageTransition><GiftCards /></PageTransition>} />
        <Route path="/membership-requirements" element={<PageTransition><DeploymentClearance /></PageTransition>} />
        <Route path="/syndicate" element={<PageTransition><SyndicatePage /></PageTransition>} />
        <Route path="/waiver/:waiverId" element={<PageTransition><Waiver /></PageTransition>} />
        <Route path="/gallery/:galleryId" element={<PageTransition><GalleryView /></PageTransition>} />
        <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
        <Route path="/admin-login" element={<PageTransition><AdminLoginPage /></PageTransition>} />
        <Route path="/manual" element={<PageTransition><Manual /></PageTransition>} />
        <Route path="/the-vessel" element={<PageTransition><EndurancePage /></PageTransition>} />
        <Route path="/OWS EXPEDITION CX2" element={<PageTransition><EndurancePage /></PageTransition>} />
        <Route path="/OWS C2 Expedition" element={<PageTransition><EndurancePage /></PageTransition>} />
        <Route path="/pre-order" element={<PageTransition><EndurancePage /></PageTransition>} />
        <Route path="/adventures" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/the-log" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/the-log/:slug" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/shop" element={<Navigate to="/expedition-cx2#configurator" replace />} />
        <Route path="/explorer-tv" element={<Navigate to="/adventures" replace />} />
        <Route path="/expeditions" element={<PageTransition><NightExpeditions /></PageTransition>} />
        <Route path="/day-trips" element={<PageTransition><GeneralExpeditions /></PageTransition>} />
        <Route path="/mission-map" element={
          <Suspense fallback={
            <div className="h-screen w-full bg-ows-dark flex items-center justify-center">
              <div className="text-ows-teal animate-pulse font-mono text-[10px] uppercase tracking-[0.3em]">Synching Satellite Uplink...</div>
            </div>
          }>
            <PageTransition><Mission3DMap /></PageTransition>
          </Suspense>
        } />
        <Route path="/admin" element={
          <Suspense fallback={
            <div className="h-screen w-full bg-ows-white flex items-center justify-center">
              <div className="text-ows-dark animate-pulse font-mono text-[10px] uppercase tracking-[0.3em]">Establishing Admin Protocol...</div>
            </div>
          }>
            <PageTransition><AdminDashboard /></PageTransition>
          </Suspense>
        } />
        <Route path="/equipment" element={<PageTransition><Equipment /></PageTransition>} />
        <Route path="/expedition-cx2" element={<PageTransition><EndurancePage /></PageTransition>} />
        <Route path="/equipment/:id" element={<PageTransition><EquipmentDetail /></PageTransition>} />
        <Route path="/membership" element={<PageTransition><Membership /></PageTransition>} />
        <Route path="/member-portal" element={<PageTransition><MemberPortal /></PageTransition>} />
        <Route path="/checkout" element={<PageTransition><MultiStepCheckout /></PageTransition>} />
        <Route path="/membership-checkout" element={<PageTransition><MembershipCheckout /></PageTransition>} />
        <Route path="/verify-prospect" element={<PageTransition><VerifyProspectPage /></PageTransition>} />
        <Route path="/booking/success" element={<PageTransition><SuccessPage /></PageTransition>} />
        <Route path="/membership/success" element={<PageTransition><SuccessPage /></PageTransition>} />
        <Route path="/gift/success" element={<PageTransition><SuccessPage /></PageTransition>} />
        <Route path="/shop/success" element={<PageTransition><SuccessPage /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        <Route path="/terms-of-use" element={<PageTransition><TermsOfUse /></PageTransition>} />
        <Route path="/full-waiver" element={<PageTransition><FullWaiver /></PageTransition>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Ignore Vite HMR websocket errors which are benign in this environment
      const message = event.reason?.message || String(event.reason);
      if (message.includes('websocket') || message.includes('vite') || message.includes('WebSocket')) {
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  return (
    <FirebaseProvider>
      <UserProvider>
        <BrandProvider>
          <CartProvider>
            <HelmetProvider>
              <Helmet>
                <title>Open Water Syndicate | SUP Lessons, Team Building & Expeditions — Toronto</title>
                <meta name="description" content="OWS trains paddlers and corporate groups on Lake Ontario from Cherry Beach. Stand up paddleboard lessons, open water certification, corporate team building, and the OWS EXPEDITION CX2 expedition board." />
                <link rel='canonical' href='https://openwatersyndicate.com/' />
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Open Water Syndicate",
                    "url": "https://openwatersyndicate.com/",
                    "logo": "https://openwatersyndicate.com/favicon.png",
                    "description": `Open water iSUP training, corporate team building, expeditions, and certification on Lake Ontario.Stand up paddleboard lessons based at Cherry Beach, Toronto.Creators of the OWS EXPEDITION CX2.`,
                    "address": {
                      "@type": "PostalAddress",
                      "streetAddress": "Cherry Beach",
                      "addressLocality": "Toronto",
                      "addressRegion": "ON",
                      "addressCountry": "CA"
                    },
                    "sameAs": [
                      "https://www.instagram.com/openwater.syndicate",
                      "https://www.youtube.com/@OWS-Canada",
                      "https://www.facebook.com/openwatersyndicate"
                    ]
                  })}
                </script>
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": "OWS EXPEDITION CX2",
                    "image": [
                      "https://firebasestorage.googleapis.com/v0/b/gen-lang-client-0348624542.firebasestorage.app/o/assets%2F1776467334781_7b22df02-8173-48ef-9855-adf0deab494e.jpg%2F1776467334781_7b22df02-8173-48ef-9855-adf0deab494e.jpg?alt=media&token=bf1f0ecb-36cb-49dc-953d-4dbd0ea4579a"
                    ],
                    "description": `Double-chamber expedition iSUP built for open water distance. Designed and tested on Lake Ontario, Toronto.`,
                    "brand": {
                      "@type": "Brand",
                      "name": "Open Water Syndicate"
                    },
                    "offers": {
                      "@type": "Offer",
                      "url": "https://openwatersyndicate.com/board",
                      "priceCurrency": "CAD",
                      "price": "999",
                      "availability": "https://schema.org/PreOrder",
                      "priceValidUntil": "2026-12-31"
                    }
                  })}
                </script>
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Open Water Syndicate",
                    "url": "https://openwatersyndicate.com/",
                    "image": "https://openwatersyndicate.com/og-image.jpg",
                    "description": `Stand up paddleboard lessons, corporate team building, open water certification and expeditions on Lake Ontario.Cherry Beach, Toronto.`,
                    "address": {
                      "@type": "PostalAddress",
                      "streetAddress": "Cherry Beach",
                      "addressLocality": "Toronto",
                      "addressRegion": "ON",
                      "postalCode": "M4M 0A1",
                      "addressCountry": "CA"
                    },
                    "geo": {
                      "@type": "GeoCoordinates",
                      "latitude": 43.6389,
                      "longitude": -79.3472
                    },
                    "openingHoursSpecification": [
                      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday", "Sunday"], "opens": "06:00", "closes": "12:00" },
                      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Friday"], "opens": "08:00", "closes": "12:00" }
                    ],
                    "priceRange": "$$",
                    "sameAs": [
                      "https://www.instagram.com/openwater.syndicate",
                      "https://www.facebook.com/openwatersyndicate"
                    ]
                  })}
                </script>
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Service",
                    "name": "Corporate Team Building — Open Water Paddleboard Expedition",
                    "provider": {
                      "@type": "Organization",
                      "name": "Open Water Syndicate",
                      "url": "https://openwatersyndicate.com/"
                    },
                    "serviceType": "Corporate Team Building",
                    "description": `4-hour guided stand up paddleboard expedition through the Toronto Islands for corporate groups.Departs Queens Quay West Marina.Maximum 8 participants. All equipment provided.Led by certified open water instructors.`,
                    "areaServed": {
                      "@type": "City",
                      "name": "Toronto"
                    },
                    "url": "https://openwatersyndicate.com/team-building",
                    "offers": {
                      "@type": "Offer",
                      "priceCurrency": "CAD",
                      "price": "1500",
                      "description": "Standard Package — up to 8 participants, 4 hours, all equipment included"
                    }

                  })}
                </script>
              </Helmet>
              <Router>
                <ScrollToAnchor />
                <div className="min-h-screen bg-ows-white text-ows-dark selection:bg-ows-teal selection:text-white relative">
                  <div className="grain-overlay" />
                  <Navbar />
                  <CartDrawer />
                  <main>
                    <AnimatedRoutes />
                  </main>
                  <Footer />
                </div>
              </Router>
            </HelmetProvider>
          </CartProvider>
        </BrandProvider>
      </UserProvider>
    </FirebaseProvider>
  );
}

